import usePagedCollection from '../../hooks/usePagedCollection';
import InfiniteScrollSentinel from '../common/InfiniteScrollSentinel';

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Icon } from 'semantic-ui-react';
import ConfirmModal from '../common/ConfirmModal';
import { useLookbookEntryPageQuery } from '../../redux/apis/endpoints/lookbookApi';
import { useVTOResults } from '../../hooks/useVTOResults';
import LookbookEntryCard from '../lookbook-panel-view/LookbookEntryCard';
import ManageLookbooksModal from '../manage-lookbooks-modal/ManageLookbooksModal';
import { useShare } from '../../context/ShareContext';
import SelectionActionBar from '../selection-action-bar/SelectionActionBar';
import { getEntryPreviewUrl } from '../../utils/lookbookEntryImage';
import './LookbookItemsPanel.css';

const LookbookItemsPanel = ({
    lookbookId,
    onCardClick,
    panelHeight = 250,
    panelPosition = 'right',
    disabled = false,
    // Bulk selection props
    isBulkSelectMode = false,
    selectedItems = [],
    onItemSelect,
    onBulkEditClick,

    onAddToLookbookClick // For adding to other lookbooks
}) => {
    // For default lookbook (null), use VTO results directly (matches LookbookPanelView behavior)
    const { vtoResults, selectedVTOResultId, deleteVTOResult, isDeleting } = useVTOResults();

    const page = usePagedCollection(useLookbookEntryPageQuery, { id: lookbookId || 'default' });
    const { items: allEntries, isLoading, isFetching } = page;

    const newestResultId = vtoResults[0]?.id;
    const previousNewest = useRef(newestResultId);
    const reloadEntries = page.reload;
    useEffect(() => {
        if (newestResultId !== previousNewest.current) reloadEntries();
        previousNewest.current = newestResultId;
    }, [newestResultId, reloadEntries]);

    // Local state for modals
    const [isManageLookbooksOpen, setIsManageLookbooksOpen] = useState(false);
    const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
    const [localSelectedItems, setLocalSelectedItems] = useState([]);
    const [itemToDelete, setItemToDelete] = useState(null); // Track single item to delete

    // Use parent's selectedItems if provided, otherwise use local state
    const effectiveSelectedItems = selectedItems.length > 0 ? selectedItems : localSelectedItems;

    const { openShare } = useShare();

    // Share the selected looks as one bundle. The recipient gets each look
    // image in their clothing panel plus the garments behind it.
    const handleShareSelection = useCallback(() => {
        if (!effectiveSelectedItems.length) return;

        const selected = allEntries.filter(entry => effectiveSelectedItems.includes(entry.id));

        openShare({
            sourceType: 'selection',
            vtoResultIds: selected.map(entry => entry.id),
            itemCount: selected.length,
            previewImages: selected.map(getEntryPreviewUrl).filter(Boolean)
        });
    }, [effectiveSelectedItems, allEntries, openShare]);

    const gridRef = useRef(null);
    const entries = allEntries;

    // Check if we have items
    const hasItems = allEntries.length > 0;

    // Handle item selection in bulk mode
    const handleItemSelect = (entryId) => {
        if (onItemSelect) {
            onItemSelect(entryId);
        } else {
            setLocalSelectedItems(prev =>
                prev.includes(entryId)
                    ? prev.filter(id => id !== entryId)
                    : [...prev, entryId]
            );
        }
    };

    // Handle delete (bulk or single)
    const handleDeleteConfirm = async () => {
        try {
            if (itemToDelete) {
                // Delete single item
                await deleteVTOResult(itemToDelete);
                setItemToDelete(null);
            } else {
                // Bulk delete
                await Promise.all(effectiveSelectedItems.map(id => deleteVTOResult(id)));

                // Clear local selection and notify parent if needed
                setLocalSelectedItems([]);
                if (isBulkSelectMode && onBulkEditClick) {
                    onBulkEditClick(); // Exit bulk mode
                }
            }
            page.reload();
            setBulkDeleteConfirmOpen(false);
        } catch (error) {
            console.error('Failed to delete:', error);
        }
    };

    const handleSingleDelete = (entry) => {
        setItemToDelete(entry.id);
        setBulkDeleteConfirmOpen(true);
    };

    // Show loading state
    if (isLoading) {
        return (
            <div className="lookbook-items-panel-container">
                {/* Redundant Bulk Edit button removed because layout header now provides 'Select' */}

                <div className="loading-state">
                    <Icon loading name="spinner" size="large" />
                </div>
            </div>
        );
    }

    // Empty state
    if (!hasItems && page.isError) return <InfiniteScrollSentinel error onRetry={page.refetch} />;
    if (!hasItems) {
        return (
            <div className="lookbook-items-panel-container">
                <div
                    className={`empty-lookbook-state ${panelPosition === 'bottom' ? 'bottom-panel-empty' : ''}`}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        minHeight: panelPosition === 'bottom' ? 'auto' : Math.min(300, panelHeight || 300),
                        padding: panelPosition === 'bottom' ? '16px' : '24px',
                        textAlign: 'center',
                        gap: '16px'
                    }}
                >
                    <Icon name="images outline" size={panelPosition === 'bottom' ? 'big' : 'huge'} color="grey" />
                    <p>No looks in this lookbook yet.</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`lookbook-items-panel-container ${isBulkSelectMode && effectiveSelectedItems.length > 0 ? 'bulk-mode-padding' : ''}`}>
            {/* Redundant Bulk Edit button removed because layout header now provides 'Select' */}


            <div ref={gridRef} className={`lookbook-entries-grid position-${panelPosition}`}>
                {entries.map(entry => (
                    <LookbookEntryCard
                        key={entry.id}
                        entry={entry}
                        onClick={() => isBulkSelectMode ? handleItemSelect(entry.id) : onCardClick(entry)}
                        onDelete={handleSingleDelete}
                        isBulkMode={isBulkSelectMode}
                        isSelected={effectiveSelectedItems.includes(entry.id)}
                        isActive={selectedVTOResultId === entry.id}
                        isHorizontal={panelPosition === 'bottom'}
                    />
                ))}
                <InfiniteScrollSentinel hasMore={page.hasMore} loading={isFetching} error={page.isError}
                    onLoadMore={page.loadMore} onRetry={page.refetch} count={entries.length} />
            </div>

            {/* Bottom Action Panel - shows when items are selected in bulk mode */}
            {isBulkSelectMode && (
                <SelectionActionBar
                    count={effectiveSelectedItems.length}
                    actions={[
                        {
                            key: 'lookbook',
                            icon: 'folder open outline',
                            label: 'lookbook',
                            onClick: () => setIsManageLookbooksOpen(true)
                        },
                        {
                            key: 'share',
                            icon: 'share alternate',
                            label: 'share',
                            onClick: handleShareSelection
                        }
                    ]}
                    destructive={{
                        icon: 'trash alternate outline',
                        label: 'delete',
                        onClick: () => setBulkDeleteConfirmOpen(true)
                    }}
                />
            )}

            {/* Manage Lookbooks Modal */}
            <ManageLookbooksModal
                open={isManageLookbooksOpen}
                onClose={() => setIsManageLookbooksOpen(false)}
                selectedResultIds={effectiveSelectedItems}
                onSuccess={() => {
                    setIsManageLookbooksOpen(false);
                    setLocalSelectedItems([]);
                }}
            />

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={bulkDeleteConfirmOpen}
                onClose={() => {
                    setBulkDeleteConfirmOpen(false);
                    setItemToDelete(null);
                }}
                onConfirm={handleDeleteConfirm}
                title={`Delete Look${(!itemToDelete && effectiveSelectedItems.length > 1) ? 's' : ''}`}
                titleIconName="trash"
                titleIconColor="red"
                variant="danger"
                isLoading={isDeleting}
                loadingText="Deleting..."
                confirmText="Delete"
            >
                <p>
                    Are you sure you want to delete {itemToDelete ? 'this look' : `${effectiveSelectedItems.length} look${effectiveSelectedItems.length > 1 ? 's' : ''}`}?
                </p>
            </ConfirmModal>
        </div>
    );
};

export default LookbookItemsPanel;
